import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { BarCard, HorizontalBarCard, DonutCard, LineCard, COLORS as CHART_COLORS } from "@/components/charts/DashboardCharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  MoreHorizontal, Plus, Send, CheckCircle2, XCircle, Ban, Trash2, FileText, Printer,
  Wallet, ShoppingCart, Receipt, HandCoins, CreditCard, Landmark, CheckCircle, FileArchive,
  ArrowLeftRight, TrendingUp, TrendingDown,
} from "lucide-react";
import { toast } from "sonner";
import { formatDateTimeID, formatRupiah, formatDateID } from "@/lib/format";
import { openTxnDocument } from "./printDocs";

const reqIdOf = (form, setForm) => {
  const rid = form.client_request_id || (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`);
  if (!form.client_request_id) setForm((f) => (f ? { ...f, client_request_id: rid } : f));
  return rid;
};

const STATUS_STYLE = {
  DRAF: "bg-slate-100 text-slate-700",
  MENUNGGU_PERSETUJUAN: "bg-amber-100 text-amber-800",
  DISETUJUI: "bg-emerald-100 text-emerald-700",
  DITOLAK: "bg-rose-100 text-rose-700",
  DIPROSES: "bg-sky-100 text-sky-700",
  SEBAGIAN: "bg-sky-100 text-sky-700",
  SELESAI: "bg-emerald-100 text-emerald-700",
  LUNAS: "bg-emerald-100 text-emerald-700",
  DIBAYAR_SEBAGIAN: "bg-amber-100 text-amber-800",
  BELUM_DIBAYAR: "bg-slate-100 text-slate-700",
  JATUH_TEMPO: "bg-amber-100 text-amber-800",
  TERLAMBAT: "bg-rose-100 text-rose-700",
  DIBATALKAN: "bg-slate-100 text-slate-500",
  DIPOSTING: "bg-indigo-100 text-indigo-700",
};

const STATUS_LABEL = {
  DRAF: "Draf",
  MENUNGGU_PERSETUJUAN: "Diajukan",
  DISETUJUI: "Disetujui",
  DITOLAK: "Ditolak",
  DIPROSES: "Diproses",
  SEBAGIAN: "Sebagian",
  SELESAI: "Selesai",
  LUNAS: "Lunas",
  DIBAYAR_SEBAGIAN: "Dibayar Sebagian",
  BELUM_DIBAYAR: "Belum Dibayar",
  JATUH_TEMPO: "Jatuh Tempo",
  TERLAMBAT: "Terlambat",
  DIBATALKAN: "Dibatalkan",
  DIPOSTING: "Terposting",
};

export function StatusBadge({ status }) {
  return <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_STYLE[status] || "bg-slate-100 text-slate-700"}`}>{STATUS_LABEL[status] || status}</span>;
}

// -------- Line items editor --------
function LineItemsEditor({ lines, setLines, products, taxes }) {
  const add = () => setLines([...(lines || []), { product_sku: "", description: "", qty: 1, price: 0, discount_pct: 0, discount_amount: 0, tax_code: "" }]);
  const update = (i, patch) => {
    const cp = [...lines]; cp[i] = { ...cp[i], ...patch }; setLines(cp);
  };
  const remove = (i) => setLines(lines.filter((_, x) => x !== i));
  const onProduct = (i, sku) => {
    const p = products.find((x) => x.sku === sku);
    update(i, { product_sku: sku, description: p?.name || "", price: p?.default_sell_price ?? p?.default_buy_price ?? 0, unit: p?.sales_unit, tax_code: p?.tax_code || "" });
  };
  return (
    <div className="space-y-2">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Baris Produk</div>
      <div className="overflow-x-auto border border-slate-200 rounded-md">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="text-left px-2 py-2 font-medium">Produk</th>
              <th className="text-right px-2 py-2 font-medium w-24">Qty</th>
              <th className="text-right px-2 py-2 font-medium w-24" title="Opsional — kosongkan untuk memakai konversi produk (qty per karton)">Unit/Box</th>
              <th className="text-right px-2 py-2 font-medium w-32">Harga</th>
              <th className="text-right px-2 py-2 font-medium w-24">Disk %</th>
              <th className="text-right px-2 py-2 font-medium w-32">Disk Nominal</th>
              <th className="text-left px-2 py-2 font-medium w-32">Pajak</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {(lines || []).map((ln, i) => (
              <tr key={i} className="border-t border-slate-100">
                <td className="px-2 py-1">
                  <Select value={ln.product_sku || ""} onValueChange={(v) => onProduct(i, v)}>
                    <SelectTrigger data-testid={`line-product-${i}`}><SelectValue placeholder="Pilih produk" /></SelectTrigger>
                    <SelectContent>
                      {products.map((p) => <SelectItem key={p.sku} value={p.sku}>{p.sku} — {p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-2 py-1"><Input type="number" min="0" value={ln.qty} onChange={(e) => update(i, { qty: parseFloat(e.target.value || 0) })} className="text-right" /></td>
                <td className="px-2 py-1"><Input type="number" min="0" placeholder="otomatis" value={ln.box_qty ?? ""} onChange={(e) => update(i, { box_qty: e.target.value === "" ? null : parseFloat(e.target.value) })} className="text-right" data-testid={`line-box-${i}`} /></td>
                <td className="px-2 py-1"><Input type="number" min="0" value={ln.price} onChange={(e) => update(i, { price: parseFloat(e.target.value || 0) })} className="text-right font-mono-num" /></td>
                <td className="px-2 py-1"><Input type="number" min="0" max="100" value={ln.discount_pct} onChange={(e) => update(i, { discount_pct: parseFloat(e.target.value || 0) })} className="text-right" /></td>
                <td className="px-2 py-1"><Input type="number" min="0" value={ln.discount_amount} onChange={(e) => update(i, { discount_amount: parseFloat(e.target.value || 0) })} className="text-right font-mono-num" /></td>
                <td className="px-2 py-1">
                  <Select value={ln.tax_code || ""} onValueChange={(v) => update(i, { tax_code: v === "__none__" ? "" : v })}>
                    <SelectTrigger><SelectValue placeholder="Tanpa Pajak" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Tanpa Pajak</SelectItem>
                      {taxes.map((t) => <SelectItem key={t.code} value={t.code}>{t.code} ({t.rate}%)</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-1"><Button type="button" size="icon" variant="ghost" onClick={() => remove(i)}><Trash2 className="h-4 w-4 text-rose-500" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Button type="button" variant="outline" size="sm" onClick={add} data-testid="line-add"><Plus className="h-4 w-4 mr-1" /> Tambah Baris</Button>
    </div>
  );
}

// -------- Line txn form (SO/Invoice/PO/Bill/Note) --------
function NumberPreview({ txnKey, partyCode, date, number }) {
  const [text, setText] = useState("");
  const [err, setErr] = useState(false);
  useEffect(() => {
    if (number || !partyCode || !date) { setText(""); return; }
    api.get("/numbering/preview", { params: { txn_key: txnKey, party_code: partyCode, date } })
      .then(({ data }) => { setErr(false); setText(`Pratinjau nomor: ${data.number}`); })
      .catch((e) => { setErr(true); setText(formatApiError(e)); });
  }, [txnKey, partyCode, date, number]);
  if (number) return <div className="text-sm font-mono text-slate-700" data-testid="txn-number-display">Nomor: {number}</div>;
  return text ? <div className={`text-sm ${err ? "text-rose-600 font-medium" : "font-mono text-slate-600"}`} data-testid="txn-number-preview">{text}</div> : null;
}

function LineTxnForm({ open, onClose, masterKey, initial, onSaved, config }) {
  const [form, setForm] = useState(initial);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [creditWarning, setCreditWarning] = useState(null);

  useEffect(() => { setForm(initial); }, [initial]);
  useEffect(() => {
    Promise.all([
      api.get("/masters/customers"), api.get("/masters/suppliers"),
      api.get("/masters/products"), api.get("/masters/taxes"),
    ]).then(([c, s, p, t]) => {
      setCustomers(c.data.filter((x) => x.status === "ACTIVE"));
      setSuppliers(s.data.filter((x) => x.status === "ACTIVE"));
      setProducts(p.data.filter((x) => x.status === "ACTIVE"));
      setTaxes(t.data.filter((x) => x.status === "ACTIVE"));
    }).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const isSales = ["sales_order","sales_invoice","sales_credit_note"].includes(masterKey);
  const parties = isSales ? customers : suppliers;

  useEffect(() => {
    if (isSales && form?.party_code) {
      api.get(`/txn/credit-check/${form.party_code}`).then(({ data }) => setCreditWarning(data.over_limit ? data : null)).catch(() => {});
    } else setCreditWarning(null);
  }, [form?.party_code, isSales]);

  const submit = async () => {
    try {
      const payload = { ...form };
      if (form.id) {
        await api.put(`/txn/entries/${masterKey}/item/${form.id}`, { ...payload, _version: form.version });
      } else {
        await api.post(`/txn/entries/${masterKey}`, { ...payload, client_request_id: reqIdOf(form, setForm) });
      }
      toast.success(`${config.label} berhasil disimpan.`);
      onSaved();
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!form) return null;

  const total = (form.lines || []).reduce((acc, ln) => {
    const qty = parseFloat(ln.qty || 0), price = parseFloat(ln.price || 0);
    const gross = qty * price;
    const disc = Math.min(gross, gross * (parseFloat(ln.discount_pct || 0) / 100) + parseFloat(ln.discount_amount || 0));
    const tax = taxes.find((t) => t.code === ln.tax_code);
    const dpp = Math.max(0, gross - disc);
    const taxAmt = tax ? dpp * (parseFloat(tax.rate) / 100) : 0;
    return acc + dpp + taxAmt;
  }, 0);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white max-w-5xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{form.id ? `Ubah ${config.label}` : `Buat ${config.label}`}</DialogTitle>
          <DialogDescription>Lengkapi informasi transaksi.</DialogDescription>
          <NumberPreview txnKey={masterKey} partyCode={form.party_code} date={form.date} number={form.number} />
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1.5"><Label>Tanggal</Label><Input type="date" value={form.date || ""} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="field-date" /></div>
          <div className="space-y-1.5 md:col-span-2">
            <Label>{isSales ? "Pelanggan" : "Pemasok"}</Label>
            <Select value={form.party_code || ""} onValueChange={(v) => { const p = parties.find((x) => x.code === v); setForm({ ...form, party_code: v, party_name: p?.name, payment_term: p?.payment_term, payment_term_days: p?.payment_term_days, currency: p?.currency || "IDR" }); }}>
              <SelectTrigger data-testid="field-party"><SelectValue placeholder={`Pilih ${isSales ? "pelanggan" : "pemasok"}`} /></SelectTrigger>
              <SelectContent>{parties.map((p) => <SelectItem key={p.code} value={p.code}>{p.code} — {p.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Nomor Referensi</Label><Input value={form.reference_number || ""} onChange={(e) => setForm({ ...form, reference_number: e.target.value })} /></div>
          <div className="space-y-1.5"><Label>Termin (Hari)</Label><Input type="number" value={form.payment_term_days ?? ""} onChange={(e) => setForm({ ...form, payment_term_days: parseInt(e.target.value || 0) })} /></div>
          <div className="space-y-1.5"><Label>Mata Uang</Label>
            <Select value={form.currency || "IDR"} onValueChange={(v) => setForm({ ...form, currency: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {["IDR","USD","EUR","SGD","JPY","CNY"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 md:col-span-3"><Label>Catatan</Label><Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
        </div>
        {creditWarning && (
          <div className="border border-rose-300 bg-rose-50 text-rose-900 rounded-md p-3 text-sm">
            ⚠️ Pelanggan telah melewati batas kredit. Sisa piutang: <b>{formatRupiah(creditWarning.outstanding)}</b>, batas: <b>{formatRupiah(creditWarning.credit_limit)}</b>.
          </div>
        )}
        <LineItemsEditor lines={form.lines || []} setLines={(v) => setForm({ ...form, lines: v })} products={products} taxes={taxes} />
        <div className="flex justify-end text-sm">
          <div className="w-64 space-y-1 border-t border-slate-200 pt-2">
            <div className="flex justify-between"><span className="text-slate-500">Estimasi Total</span><span className="font-mono-num font-semibold">{formatRupiah(total)}</span></div>
            <div className="text-[11px] text-slate-500">Perhitungan final divalidasi backend.</div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={submit} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="txn-save">Simpan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -------- Transaction list page --------
export function TransactionListPage({ masterKey, config, showActions = true }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectNote, setRejectNote] = useState("");
  const [detail, setDetail] = useState(null);

  const canManage = ["super_admin","keuangan"].includes(user?.role);
  const canApprove = ["super_admin","direktur_utama","direktur_operasional"].includes(user?.role);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/txn/entries/${masterKey}`, { params: status !== "all" ? { status } : {} });
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const openCreate = () => {
    const today = new Date().toISOString().slice(0, 10);
    setEditing({ date: today, currency: "IDR", lines: [] });
    setDialogOpen(true);
  };

  const doAction = async (item, action, note) => {
    try {
      const url = `/txn/entries/${masterKey}/item/${item.id}/${action}`;
      await api.post(url, { note: note || "" });
      toast.success("Aksi berhasil.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doReject = async () => {
    if (!rejectNote) return toast.error("Alasan wajib diisi.");
    await doAction(rejectFor, "reject", rejectNote);
    setRejectFor(null); setRejectNote("");
  };

  const soToInvoice = async (item) => {
    try {
      await api.post(`/txn/sales_order/item/${item.id}/create-invoice`, {});
      toast.success("Invoice berhasil dibuat dari pesanan.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const poToBill = async (item) => {
    try {
      const ref = window.prompt("Nomor invoice pemasok (opsional):", "");
      await api.post(`/txn/purchase_order/item/${item.id}/create-bill`, { supplier_invoice_number: ref || null });
      toast.success("Tagihan berhasil dibuat dari pesanan.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6 page-fade" data-testid={`txn-${masterKey}-page`}>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">{config.title}</h1>
          <p className="text-slate-500 mt-2">{config.description}</p>
        </div>
        {canManage && showActions && (
          <Button onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`add-${masterKey}`}>
            <Plus className="h-4 w-4 mr-1.5" /> {config.addLabel || "Buat Baru"}
          </Button>
        )}
      </div>
      <Card>
        <CardContent className="p-4 sm:p-6 space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Status</SelectItem>
                {["DRAF","MENUNGGU_PERSETUJUAN","DISETUJUI","DITOLAK","SEBAGIAN","SELESAI","DIBATALKAN"].map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={load}>Muat Ulang</Button>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nomor</TableHead>
                  <TableHead>Tanggal</TableHead>
                  <TableHead>Pihak</TableHead>
                  <TableHead>Referensi</TableHead>
                  <TableHead className="text-right">Nominal</TableHead>
                  <TableHead>Mata Uang</TableHead>
                  <TableHead>Status</TableHead>
                  {showActions && <TableHead className="text-right">Tindakan</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-500">Memuat data...</TableCell></TableRow>
                ) : items.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-500">Belum ada data.</TableCell></TableRow>
                ) : items.map((t) => (
                  <TableRow key={t.id} data-testid={`row-${t.id}`}>
                    <TableCell className="font-mono-num">{t.number}</TableCell>
                    <TableCell>{formatDateID(t.date)}</TableCell>
                    <TableCell>
                      <div className="font-medium">{t.party_name}</div>
                      <div className="text-xs text-slate-500">{t.party_code}</div>
                    </TableCell>
                    <TableCell className="text-xs">{t.reference_number || "-"}</TableCell>
                    <TableCell className="text-right font-mono-num">{formatRupiah((t.totals?.amount) ?? t.amount ?? 0)}</TableCell>
                    <TableCell>{t.currency || "IDR"}</TableCell>
                    <TableCell><StatusBadge status={t.payment_status || t.status} /></TableCell>
                    {showActions && (
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-white">
                            <DropdownMenuItem onClick={() => setDetail(t)}><FileText className="h-4 w-4 mr-2" /> Detail & Riwayat</DropdownMenuItem>
                            {canManage && ["DRAF","DITOLAK"].includes(t.status) && <DropdownMenuItem onClick={() => { setEditing(t); setDialogOpen(true); }}>Ubah</DropdownMenuItem>}
                            {canManage && ["DRAF","DITOLAK"].includes(t.status) && <DropdownMenuItem onClick={() => doAction(t, "submit")}><Send className="h-4 w-4 mr-2" /> Ajukan Persetujuan</DropdownMenuItem>}
                            {canApprove && t.status === "MENUNGGU_PERSETUJUAN" && <>
                              <DropdownMenuItem onClick={() => doAction(t, "approve")}><CheckCircle2 className="h-4 w-4 mr-2" /> Setujui</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => setRejectFor(t)} className="text-rose-600"><XCircle className="h-4 w-4 mr-2" /> Tolak</DropdownMenuItem>
                            </>}
                            {masterKey === "sales_order" && ["DISETUJUI","SEBAGIAN"].includes(t.status) && canManage && (
                              <DropdownMenuItem onClick={() => soToInvoice(t)}><Receipt className="h-4 w-4 mr-2" /> Buat Invoice</DropdownMenuItem>
                            )}
                            {masterKey === "purchase_order" && ["DISETUJUI","SEBAGIAN"].includes(t.status) && canManage && (
                              <DropdownMenuItem onClick={() => poToBill(t)}><Receipt className="h-4 w-4 mr-2" /> Buat Tagihan</DropdownMenuItem>
                            )}
                            {(canManage || canApprove) && !["DIBATALKAN"].includes(t.status) && <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => {
                                const note = window.prompt("Alasan pembatalan:", "");
                                if (note) doAction(t, "cancel", note);
                              }} className="text-rose-600"><Ban className="h-4 w-4 mr-2" /> Batalkan</DropdownMenuItem>
                            </>}
                            {["sales_invoice", "purchase_order"].includes(masterKey) && <>
                              <DropdownMenuItem onClick={() => openTxnDocument(t, masterKey, false)} data-testid={`txn-preview-${t.number}`}><FileText className="h-4 w-4 mr-2" /> Pratinjau</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openTxnDocument(t, masterKey, true)} data-testid={`txn-print-${t.number}`}><Printer className="h-4 w-4 mr-2" /> Cetak / PDF</DropdownMenuItem>
                            </>}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {dialogOpen && (
        <LineTxnForm open={dialogOpen} onClose={() => { setDialogOpen(false); setEditing(null); }}
                     masterKey={masterKey} initial={editing} onSaved={load} config={config} />
      )}
      <Dialog open={!!rejectFor} onOpenChange={(v) => !v && setRejectFor(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Alasan Penolakan</DialogTitle><DialogDescription>Wajib diisi.</DialogDescription></DialogHeader>
          <Textarea rows={3} value={rejectNote} onChange={(e) => setRejectNote(e.target.value)} placeholder="Contoh: Nominal tidak sesuai..." />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectFor(null)}>Batal</Button>
            <Button onClick={doReject} className="bg-rose-600 hover:bg-rose-700 text-white">Tolak Transaksi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DetailDialog detail={detail} onClose={() => setDetail(null)} masterKey={masterKey} />
    </div>
  );
}

function DetailDialog({ detail, onClose, masterKey }) {
  const [payload, setPayload] = useState(null);
  useEffect(() => {
    if (detail?.id) {
      api.get(`/txn/entries/${masterKey}/item/${detail.id}`).then(({ data }) => setPayload(data)).catch(() => {});
    }
  }, [detail, masterKey]);
  if (!detail) return null;
  const t = payload?.transaction || detail;
  return (
    <Dialog open={!!detail} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{t.number}</DialogTitle><DialogDescription>{formatDateID(t.date)} · {t.party_name}</DialogDescription></DialogHeader>
        <Tabs defaultValue="detail">
          <TabsList>
            <TabsTrigger value="detail">Detail</TabsTrigger>
            <TabsTrigger value="history">Riwayat</TabsTrigger>
            <TabsTrigger value="alloc">Alokasi</TabsTrigger>
          </TabsList>
          <TabsContent value="detail" className="space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Row label="Status">{<StatusBadge status={t.status} />}</Row>
              <Row label="Mata Uang">{t.currency} @ {t.exchange_rate}</Row>
              <Row label="Termin">{t.payment_term} ({t.payment_term_days} hari)</Row>
              <Row label="Jatuh Tempo">{formatDateID(t.due_date)}</Row>
              <Row label="Total">{formatRupiah((t.totals?.amount) ?? t.amount)}</Row>
              <Row label="Setara IDR">{formatRupiah(t.amount_base)}</Row>
              <Row label="Status Akuntansi">{t.accounting_status}</Row>
            </div>
            {t.lines && (
              <div className="border border-slate-200 rounded-md overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50"><tr>
                    <th className="text-left px-2 py-1">SKU</th><th className="text-left px-2 py-1">Deskripsi</th>
                    <th className="text-right px-2 py-1">Qty</th><th className="text-right px-2 py-1">Harga</th>
                    <th className="text-right px-2 py-1">Diskon</th><th className="text-right px-2 py-1">DPP</th>
                    <th className="text-right px-2 py-1">Pajak</th><th className="text-right px-2 py-1">Total</th>
                  </tr></thead>
                  <tbody>
                    {t.lines.map((l, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="px-2 py-1 font-mono-num">{l.product_sku}</td>
                        <td className="px-2 py-1">{l.description}</td>
                        <td className="px-2 py-1 text-right">{l.qty}</td>
                        <td className="px-2 py-1 text-right font-mono-num">{formatRupiah(l.price)}</td>
                        <td className="px-2 py-1 text-right font-mono-num">{formatRupiah(l.discount)}</td>
                        <td className="px-2 py-1 text-right font-mono-num">{formatRupiah(l.dpp)}</td>
                        <td className="px-2 py-1 text-right font-mono-num">{formatRupiah(l.tax_amount)} ({l.tax_rate}%)</td>
                        <td className="px-2 py-1 text-right font-mono-num font-semibold">{formatRupiah(l.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
          <TabsContent value="history">
            {payload?.history?.length ? payload.history.map((h) => (
              <div key={h.id} className="border-b border-slate-100 py-2 text-sm">
                <div className="flex justify-between text-xs text-slate-500"><span>{h.user_name}</span><span>{formatDateTimeID(h.timestamp)}</span></div>
                <div>{h.activity} {h.note && <span className="text-slate-500">— {h.note}</span>}</div>
              </div>
            )) : <div className="text-slate-500 text-sm py-3">Belum ada riwayat.</div>}
          </TabsContent>
          <TabsContent value="alloc">
            {payload?.allocations?.length ? (
              <table className="w-full text-sm">
                <thead className="bg-slate-50"><tr><th className="text-left px-2 py-1">Tanggal</th><th className="text-left px-2 py-1">Pembayaran</th><th className="text-left px-2 py-1">Target</th><th className="text-right px-2 py-1">Nominal</th></tr></thead>
                <tbody>{payload.allocations.map((a) => <tr key={a.id} className="border-t border-slate-100"><td className="px-2 py-1">{formatDateID(a.date)}</td><td className="px-2 py-1 font-mono-num">{a.payment_number}</td><td className="px-2 py-1 font-mono-num">{a.target_number}</td><td className="px-2 py-1 text-right font-mono-num">{formatRupiah(a.amount)}</td></tr>)}</tbody>
              </table>
            ) : <div className="text-slate-500 text-sm py-3">Belum ada alokasi.</div>}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, children }) {
  return <div className="flex justify-between border-b border-slate-100 py-1.5"><span className="text-slate-500">{label}</span><span className="font-medium">{children}</span></div>;
}

// -------- Payment form (receipt/payment) --------
export function PaymentPage({ masterKey, config }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [banks, setBanks] = useState([]);
  const [parties, setParties] = useState([]);
  const [subledger, setSubledger] = useState([]);
  const [form, setForm] = useState(null);
  const [detail, setDetail] = useState(null);
  const [editMode, setEditMode] = useState(false); // true when editing an existing receipt
  const [confirmSave, setConfirmSave] = useState(false);

  const isReceipt = masterKey === "customer_receipt";
  const partyEndpoint = isReceipt ? "/masters/customers" : "/masters/suppliers";
  const canManage = ["super_admin","keuangan"].includes(user?.role);
  const canEditReceipt = isReceipt && canManage;

  const load = async () => {
    try {
      const [{ data }, banksRes, partiesRes] = await Promise.all([
        api.get(`/txn/entries/${masterKey}`),
        api.get("/masters/bank_accounts"), api.get(partyEndpoint),
      ]);
      setItems(data);
      setBanks(banksRes.data.filter((b) => b.status === "ACTIVE"));
      setParties(partiesRes.data.filter((p) => p.status === "ACTIVE"));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [masterKey]);

  const openNew = () => {
    setEditMode(false);
    setForm({ date: new Date().toISOString().slice(0, 10), party_code: "", currency: "IDR", amount: 0, allocations: [], method: "Transfer", bank_or_cash_type: "BANK" });
    setSubledger([]);
  };
  const openEdit = async (rec) => {
    setEditMode(true);
    setDetail(null);
    setForm({
      id: rec.id, number: rec.number,
      date: rec.date, party_code: rec.party_code, party_name: rec.party_name,
      currency: rec.currency || "IDR", amount: rec.amount,
      allocations: (rec.allocations || []).map((a) => ({ invoice_id: a.invoice_id, amount: a.amount })),
      method: rec.method || "Transfer",
      bank_or_cash_code: rec.bank_or_cash_code, bank_or_cash_type: rec.bank_or_cash_type || "BANK",
      reference_number: rec.reference_number || "", notes: rec.notes || "",
      change_reason: "",
      accounting_status: rec.accounting_status,
    });
    // Prefill subledger with existing invoice outstandings PLUS current allocations
    try {
      const url = isReceipt ? "/txn/aging/ar" : "/txn/aging/ap";
      const { data } = await api.get(url);
      const filterKey = isReceipt ? "customer_code" : "supplier_code";
      const rows = (data.rows || []).filter((r) => r[filterKey] === rec.party_code);
      // Ensure invoices already allocated by this receipt appear in the list even if outstanding == 0
      const existingIds = new Set(rows.map((r) => r.invoice_id || r.bill_id));
      const extras = [];
      for (const a of (rec.allocations || [])) {
        if (!existingIds.has(a.invoice_id)) {
          extras.push({ invoice_id: a.invoice_id, invoice_number: a.invoice_number,
                        outstanding: a.amount, customer_code: rec.party_code });
        } else {
          // Restore outstanding as if this receipt was reversed (so user can re-edit)
          const idx = rows.findIndex((r) => (r.invoice_id || r.bill_id) === a.invoice_id);
          if (idx >= 0) rows[idx] = { ...rows[idx], outstanding: (rows[idx].outstanding || 0) + a.amount };
        }
      }
      setSubledger([...rows, ...extras]);
    } catch (e) { /* silent */ }
  };
  const changeParty = async (code) => {
    const p = parties.find((x) => x.code === code);
    setForm((f) => ({ ...f, party_code: code, party_name: p?.name, currency: p?.currency || "IDR" }));
    try {
      const url = isReceipt ? "/txn/aging/ar" : "/txn/aging/ap";
      const { data } = await api.get(url);
      const filterKey = isReceipt ? "customer_code" : "supplier_code";
      setSubledger((data.rows || []).filter((r) => r[filterKey] === code));
    } catch (e) { /* silent */ }
  };
  const setAlloc = (invId, amount) => {
    setForm((f) => {
      const others = (f.allocations || []).filter((a) => a.invoice_id !== invId);
      if (amount > 0) return { ...f, allocations: [...others, { invoice_id: invId, amount }] };
      return { ...f, allocations: others };
    });
  };
  const totalAlloc = (form?.allocations || []).reduce((s, a) => s + parseFloat(a.amount || 0), 0);

  const doSubmit = async () => {
    setConfirmSave(false);
    try {
      if (editMode && isReceipt) {
        const { id: _rid, number: _rn, accounting_status: _as, ...rest } = form;
        await api.put(`/txn/customer_receipt/${form.id}`, rest);
        toast.success(`Penerimaan ${form.number || ""} berhasil diperbarui.`);
      } else {
        const url = `/txn/${masterKey}`;
        await api.post(url, { ...form, client_request_id: reqIdOf(form, setForm) });
        toast.success(`${config.label} berhasil dicatat.`);
      }
      setForm(null); setEditMode(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const submit = () => {
    if (editMode) {
      if (!form.change_reason || form.change_reason.trim().length < 3) {
        toast.error("Alasan perubahan wajib diisi (min 3 karakter).");
        return;
      }
      setConfirmSave(true);
      return;
    }
    doSubmit();
  };

  return (
    <div className="space-y-6 page-fade" data-testid={`txn-${masterKey}-page`}>
      <div className="flex justify-between items-end">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">{config.title}</h1>
          <p className="text-slate-500 mt-2">{config.description}</p>
        </div>
        {canManage && <Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`add-${masterKey}`}><Plus className="h-4 w-4 mr-1.5" /> {isReceipt ? "Catat Penerimaan" : "Catat Pembayaran"}</Button>}
      </div>
      <Card>
        <CardContent className="p-4 sm:p-6">
          <Table>
            <TableHeader><TableRow><TableHead>Nomor</TableHead><TableHead>Tanggal</TableHead><TableHead>{isReceipt ? "Pelanggan" : "Pemasok"}</TableHead><TableHead>Metode</TableHead><TableHead className="text-right">Nominal</TableHead><TableHead>Rekening</TableHead><TableHead className="text-right">Detail</TableHead></TableRow></TableHeader>
            <TableBody>
              {items.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">Belum ada data.</TableCell></TableRow> : items.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-mono-num">{r.number}</TableCell>
                  <TableCell>{formatDateID(r.date)}</TableCell>
                  <TableCell>{r.party_name}</TableCell>
                  <TableCell>{r.method}</TableCell>
                  <TableCell className="text-right font-mono-num">{formatRupiah(r.amount)}</TableCell>
                  <TableCell className="text-xs">{r.bank_or_cash_code}</TableCell>
                  <TableCell className="text-right"><Button variant="ghost" size="sm" onClick={() => setDetail(r)}>Lihat</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {form && (
        <Dialog open={!!form} onOpenChange={(v) => !v && (setForm(null), setEditMode(false))}>
          <DialogContent className="bg-white max-w-3xl max-h-[92vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle data-testid="payment-dialog-title">
                {editMode
                  ? (isReceipt ? `Edit Penerimaan ${form.number || ""}` : `Edit Pembayaran ${form.number || ""}`)
                  : (isReceipt ? "Catat Penerimaan Pelanggan" : "Catat Pembayaran Pemasok")}
              </DialogTitle>
            </DialogHeader>
            {editMode && (
              <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3" data-testid="edit-warning">
                <strong>Perhatian:</strong> Mengubah penerimaan akan me-revisi alokasi ke faktur pelanggan dan mempengaruhi buku besar piutang. Penerimaan yang sudah diposting ke Buku Besar atau dicocokkan dengan mutasi bank tidak dapat diubah langsung.
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Tanggal</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="payment-date" /></div>
              <div className="space-y-1.5"><Label>{isReceipt ? "Pelanggan" : "Pemasok"}</Label>
                <Select value={form.party_code} onValueChange={changeParty} disabled={editMode}>
                  <SelectTrigger data-testid="payment-party"><SelectValue placeholder="Pilih..." /></SelectTrigger>
                  <SelectContent>{parties.map((p) => <SelectItem key={p.code} value={p.code}>{p.code} — {p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Rekening Bank/Kas</Label>
                <Select value={form.bank_or_cash_code || ""} onValueChange={(v) => setForm({ ...form, bank_or_cash_code: v })}>
                  <SelectTrigger data-testid="payment-bank"><SelectValue placeholder="Pilih rekening" /></SelectTrigger>
                  <SelectContent>{banks.map((b) => <SelectItem key={b.code} value={b.code}>{b.code} — {b.bank_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Metode</Label>
                <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                  <SelectTrigger data-testid="payment-method"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Transfer","Tunai","Cek","Giro","Debit","Kartu Kredit"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Nominal</Label><Input type="number" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value || 0) })} className="font-mono-num" data-testid="payment-amount" /></div>
              <div className="space-y-1.5"><Label>Referensi Bank</Label><Input value={form.reference_number || ""} onChange={(e) => setForm({ ...form, reference_number: e.target.value })} data-testid="payment-reference" /></div>
              <div className="space-y-1.5 col-span-2"><Label>Keterangan</Label><Input value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="payment-notes" placeholder="Opsional" /></div>
              {editMode && (
                <div className="space-y-1.5 col-span-2">
                  <Label>Alasan Perubahan <span className="text-red-600">*</span></Label>
                  <Input value={form.change_reason || ""}
                         onChange={(e) => setForm({ ...form, change_reason: e.target.value })}
                         placeholder="Contoh: salah input nominal / tanggal"
                         data-testid="payment-change-reason" />
                </div>
              )}
            </div>
            {form.party_code && (
              <div className="space-y-2">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Alokasikan ke {isReceipt ? "Invoice" : "Tagihan"}</div>
                <table className="w-full text-sm border border-slate-200 rounded-md">
                  <thead className="bg-slate-50"><tr><th className="px-2 py-1 text-left">Nomor</th><th className="px-2 py-1 text-right">Sisa</th><th className="px-2 py-1 text-right">Alokasi</th></tr></thead>
                  <tbody>
                    {subledger.length === 0 ? <tr><td colSpan={3} className="text-center text-slate-500 py-4">Tidak ada outstanding.</td></tr> : subledger.map((row) => {
                      const id = row.invoice_id || row.bill_id;
                      const number = row.invoice_number || row.bill_number;
                      const alloc = form.allocations.find((a) => a.invoice_id === id)?.amount || 0;
                      return (
                        <tr key={id} className="border-t border-slate-100">
                          <td className="px-2 py-1 font-mono-num">{number}</td>
                          <td className="px-2 py-1 text-right font-mono-num">{formatRupiah(row.outstanding)}</td>
                          <td className="px-2 py-1 text-right"><Input type="number" min="0" max={row.outstanding} value={alloc} onChange={(e) => setAlloc(id, parseFloat(e.target.value || 0))} className="text-right font-mono-num" /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot><tr className="bg-slate-50"><td colSpan={2} className="px-2 py-1 text-right font-semibold">Total Alokasi</td><td className="px-2 py-1 text-right font-mono-num font-semibold">{formatRupiah(totalAlloc)}</td></tr></tfoot>
                </table>
                {form.amount > totalAlloc && <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2">Selisih {formatRupiah(form.amount - totalAlloc)} akan dicatat sebagai uang muka.</div>}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => { setForm(null); setEditMode(false); }} data-testid="payment-cancel">Batal</Button>
              <Button onClick={submit} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="payment-save">
                {editMode ? "Simpan Perubahan" : "Simpan"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Confirmation before saving an edit */}
      <Dialog open={confirmSave} onOpenChange={setConfirmSave}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle>Konfirmasi Perubahan Penerimaan</DialogTitle></DialogHeader>
          <div className="text-sm text-slate-700">
            Perubahan pada penerimaan <strong>{form?.number}</strong> akan
            merevisi alokasi ke faktur pelanggan dan mempengaruhi buku besar piutang.
            Apakah Anda yakin ingin melanjutkan?
          </div>
          <div className="text-xs text-slate-500 mt-2">Alasan: {form?.change_reason}</div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmSave(false)} data-testid="payment-confirm-cancel">Batal</Button>
            <Button onClick={doSubmit} className="bg-red-600 hover:bg-red-700 text-white" data-testid="payment-confirm-save">Ya, Simpan Perubahan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader><DialogTitle>{detail?.number}</DialogTitle></DialogHeader>
          {detail && (<div className="space-y-2 text-sm">
            <Row label="Tanggal">{formatDateID(detail.date)}</Row>
            <Row label={isReceipt ? "Pelanggan" : "Pemasok"}>{detail.party_name}</Row>
            <Row label="Rekening">{detail.bank_or_cash_code}</Row>
            <Row label="Metode">{detail.method}</Row>
            <Row label="Nominal">{formatRupiah(detail.amount)}</Row>
            <Row label="Lebih Bayar">{formatRupiah(detail.overpayment || 0)}</Row>
            {detail.notes && <Row label="Keterangan">{detail.notes}</Row>}
            {detail.reference_number && <Row label="Referensi">{detail.reference_number}</Row>}
            <Row label="Status Akuntansi">
              <span className={detail.accounting_status === "DIPOSTING" ? "text-emerald-700 font-semibold" : "text-slate-700"}>
                {detail.accounting_status || "-"}
              </span>
            </Row>
            <div className="pt-2">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Alokasi</div>
              {(detail.allocations || []).map((a, i) => <div key={i} className="flex justify-between text-xs border-b border-slate-100 py-1"><span className="font-mono-num">{a.invoice_number || a.bill_number}</span><span className="font-mono-num">{formatRupiah(a.amount)}</span></div>)}
            </div>
            {detail.last_change_reason && (
              <div className="text-xs text-slate-500 pt-2 border-t border-slate-100">
                <strong>Perubahan terakhir:</strong> {detail.last_change_reason}
              </div>
            )}
          </div>)}
          {canEditReceipt && detail && detail.accounting_status !== "DIPOSTING" && !detail.bank_matched && (
            <DialogFooter>
              <Button variant="outline"
                      onClick={() => openEdit(detail)}
                      data-testid={`edit-${masterKey}-${detail.id}`}
                      className="border-amber-500 text-amber-700 hover:bg-amber-50">
                Edit Penerimaan
              </Button>
            </DialogFooter>
          )}
          {canEditReceipt && detail && (detail.accounting_status === "DIPOSTING" || detail.bank_matched) && (
            <div className="text-xs text-slate-500 border-t border-slate-100 pt-2">
              Penerimaan ini sudah {detail.accounting_status === "DIPOSTING" ? "diposting ke Buku Besar" : "dicocokkan dengan mutasi bank"} — tidak dapat diubah langsung.
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// -------- Aging views --------
export function AgingPage({ kind }) {
  const [data, setData] = useState(null);
  const isAR = kind === "ar";
  useEffect(() => {
    api.get(`/txn/aging/${kind}`).then(({ data }) => setData(data)).catch(() => {});
  }, [kind]);
  const partyField = isAR ? "customer_name" : "supplier_name";
  const topParties = React.useMemo(() => {
    const agg = {};
    (data?.rows || []).forEach((r) => {
      const key = r[partyField] || "(Tanpa Nama)";
      agg[key] = (agg[key] || 0) + (Number(r.outstanding) || 0);
    });
    return Object.entries(agg)
      .filter(([, v]) => v > 0)
      .sort((a, b2) => b2[1] - a[1])
      .slice(0, 5)
      .map(([name, value]) => ({ name, value }));
  }, [data, partyField]);
  if (!data) return <div className="text-slate-500 page-fade">Memuat data...</div>;
  const b = data.buckets;
  const total = (b?.not_due || 0) + (b?.["1_30"] || 0) + (b?.["31_60"] || 0) + (b?.["61_90"] || 0) + (b?.over_90 || 0);
  return (
    <div className="space-y-6 page-fade" data-testid={`aging-${kind}-page`}>
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">{isAR ? "Piutang Usaha" : "Hutang Usaha"}</h1>
        <p className="text-slate-500 mt-2">Aging {isAR ? "piutang pelanggan" : "hutang pemasok"} berdasarkan jatuh tempo.</p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          ["not_due","Belum Jatuh Tempo","bg-emerald-50 border-emerald-200 text-emerald-800"],
          ["1_30","1-30 Hari","bg-amber-50 border-amber-200 text-amber-800"],
          ["31_60","31-60 Hari","bg-amber-50 border-amber-200 text-amber-800"],
          ["61_90","61-90 Hari","bg-rose-50 border-rose-200 text-rose-800"],
          ["over_90","> 90 Hari","bg-rose-50 border-rose-200 text-rose-800"],
        ].map(([k, label, cls]) => (
          <div key={k} className={`rounded-md border p-4 ${cls}`}><div className="text-xs opacity-80">{label}</div><div className="font-mono-num text-lg font-semibold mt-1">{formatRupiah(b[k])}</div></div>
        ))}
      </div>

      {/* Phase 2 charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <BarCard
          title={`Aging ${isAR ? "Piutang" : "Hutang"}`}
          testId={`chart-aging-${kind}`}
          currency
          hint={`Belum ada data ${isAR ? "piutang" : "hutang"} untuk periode ini.`}
          data={total <= 0 ? [] : [
            { name: "Belum Jatuh Tempo", value: b.not_due   || 0 },
            { name: "1-30 Hari",         value: b["1_30"]   || 0 },
            { name: "31-60 Hari",        value: b["31_60"]  || 0 },
            { name: "61-90 Hari",        value: b["61_90"]  || 0 },
            { name: "> 90 Hari",         value: b.over_90   || 0 },
          ]}
          bars={[{ key: "value", label: isAR ? "Piutang" : "Hutang", color: CHART_COLORS.baseline }]}
        />
        <HorizontalBarCard
          title={`Top 5 ${isAR ? "Pelanggan" : "Pemasok"} ${isAR ? "Piutang" : "Hutang"}`}
          testId={`chart-top-${kind}`}
          currency
          hint={`Belum ada data ${isAR ? "pelanggan" : "pemasok"} dengan outstanding.`}
          data={topParties}
          bars={[{ key: "value", label: "Outstanding", color: CHART_COLORS.risk }]}
        />
      </div>
      <Card><CardContent className="p-4 sm:p-6">
        <Table><TableHeader><TableRow>
          <TableHead>{isAR ? "Invoice" : "Tagihan"}</TableHead>
          <TableHead>{isAR ? "Pelanggan" : "Pemasok"}</TableHead>
          <TableHead>Tanggal</TableHead><TableHead>Jatuh Tempo</TableHead>
          <TableHead className="text-right">Nilai</TableHead><TableHead className="text-right">Sisa</TableHead>
          <TableHead className="text-right">Umur (hari)</TableHead><TableHead>Status</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {data.rows.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-8">Tidak ada outstanding.</TableCell></TableRow> : data.rows.map((r) => (
            <TableRow key={r.id}>
              <TableCell className="font-mono-num">{isAR ? r.invoice_number : r.bill_number}</TableCell>
              <TableCell>{isAR ? r.customer_name : r.supplier_name}</TableCell>
              <TableCell>{formatDateID(r.date)}</TableCell>
              <TableCell>{formatDateID(r.due_date)}</TableCell>
              <TableCell className="text-right font-mono-num">{formatRupiah(r.total_amount)}</TableCell>
              <TableCell className="text-right font-mono-num font-semibold">{formatRupiah(r.outstanding)}</TableCell>
              <TableCell className="text-right font-mono-num">{r.days_overdue}</TableCell>
              <TableCell><StatusBadge status={r.status} /></TableCell>
            </TableRow>
          ))}
        </TableBody></Table>
      </CardContent></Card>
    </div>
  );
}

// -------- Approval inbox --------
export function ApprovalInboxPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/txn/approvals/inbox"); setRows(data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const act = async (row, action, note) => {
    try {
      await api.post(`/txn/entries/${row.key}/item/${row.id}/${action}`, { note: note || "" });
      toast.success("Aksi berhasil.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-6 page-fade" data-testid="approval-inbox-page">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Persetujuan Saya</h1>
        <p className="text-slate-500 mt-2">Daftar transaksi yang menunggu persetujuan Anda.</p>
      </div>
      <Card><CardContent className="p-4 sm:p-6">
        <Table><TableHeader><TableRow>
          <TableHead>Jenis</TableHead><TableHead>Nomor</TableHead><TableHead>Tanggal</TableHead>
          <TableHead>Pihak</TableHead><TableHead className="text-right">Nominal</TableHead>
          <TableHead>Diajukan Oleh</TableHead><TableHead className="text-right">Tindakan</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {loading ? <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">Memuat data...</TableCell></TableRow> :
           rows.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">Tidak ada transaksi menunggu persetujuan.</TableCell></TableRow> :
           rows.map((r) => (
            <TableRow key={r.id}>
              <TableCell>{r.label}</TableCell>
              <TableCell className="font-mono-num">{r.number}</TableCell>
              <TableCell>{formatDateID(r.date)}</TableCell>
              <TableCell>{r.party_name}</TableCell>
              <TableCell className="text-right font-mono-num">{formatRupiah(r.amount_base || r.amount)}</TableCell>
              <TableCell className="text-xs">{r.submitted_by}</TableCell>
              <TableCell className="text-right">
                <Button size="sm" onClick={() => act(r, "approve")} className="bg-emerald-600 hover:bg-emerald-700 text-white mr-2" data-testid={`approve-${r.id}`}>Setujui</Button>
                <Button size="sm" variant="outline" className="text-rose-600" onClick={() => { const n = window.prompt("Alasan penolakan:", ""); if (n) act(r, "reject", n); }}>Tolak</Button>
              </TableCell>
            </TableRow>
           ))}
        </TableBody></Table>
      </CardContent></Card>
    </div>
  );
}

// -------- Payment Center --------
export function PaymentCenterPage() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/txn/payment-center").then(({ data }) => setRows(data)).catch(() => {}); }, []);
  return (
    <div className="space-y-6 page-fade" data-testid="payment-center-page">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Pusat Pembayaran</h1>
        <p className="text-slate-500 mt-2">Kewajiban pembayaran yang mendekati atau melewati jatuh tempo.</p>
      </div>
      <Card><CardContent className="p-4 sm:p-6">
        <Table><TableHeader><TableRow>
          <TableHead>Jenis</TableHead><TableHead>Pihak</TableHead><TableHead>Referensi</TableHead>
          <TableHead>Jatuh Tempo</TableHead><TableHead className="text-right">Outstanding</TableHead>
          <TableHead>Prioritas</TableHead><TableHead>Status</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {rows.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">Tidak ada kewajiban pembayaran.</TableCell></TableRow> : rows.map((r, i) => (
            <TableRow key={i}>
              <TableCell>{r.type}</TableCell><TableCell>{r.party}</TableCell><TableCell className="font-mono-num">{r.reference}</TableCell>
              <TableCell>{formatDateID(r.due_date)}</TableCell>
              <TableCell className="text-right font-mono-num">{formatRupiah(r.amount)}</TableCell>
              <TableCell><Badge className={r.priority === "Mendesak" ? "bg-rose-600" : r.priority === "Penting" ? "bg-amber-500" : "bg-slate-500"}>{r.priority}</Badge></TableCell>
              <TableCell><StatusBadge status={r.status} /></TableCell>
            </TableRow>
          ))}
        </TableBody></Table>
      </CardContent></Card>
    </div>
  );
}

// -------- Module hubs --------
function ModuleHub({ title, description, tiles }) {
  const navigate = useNavigate();
  return (
    <div className="space-y-6 page-fade">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">{title}</h1>
        <p className="text-slate-500 mt-2">{description}</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {tiles.map((t) => (
          <button key={t.path} onClick={() => navigate(t.path)}
            className="text-left bg-white border border-slate-200 rounded-lg p-5 hover:border-emerald-500 hover:shadow-sm transition-colors"
            data-testid={`hub-tile-${t.path.replace(/\//g, "-")}`}>
            <div className="h-10 w-10 rounded-lg bg-slate-900 text-white grid place-items-center mb-3"><t.icon className="h-5 w-5" /></div>
            <div className="font-display font-semibold">{t.label}</div>
            <div className="text-xs text-slate-500 mt-1">{t.desc}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

// -------- Dashboards --------
function DashCard({ label, value, hint, accent = "slate" }) {
  const bg = { emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
               amber: "bg-amber-50 text-amber-800 border-amber-200",
               slate: "bg-slate-50 text-slate-800 border-slate-200",
               rose: "bg-rose-50 text-rose-800 border-rose-200" }[accent];
  return (
    <div className={`border rounded-md p-5 ${bg}`}>
      <div className="text-xs opacity-80">{label}</div>
      <div className="font-mono-num text-2xl font-semibold mt-1">{value}</div>
      {hint && <div className="text-xs opacity-70 mt-1">{hint}</div>}
    </div>
  );
}

export function SalesHub() {
  const [d, setD] = useState({});
  const [ts, setTs] = useState(null);
  useEffect(() => { api.get("/txn/dashboard/sales").then(({ data }) => setD(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/txn/dashboard/sales/timeseries?granularity=month").then(({ data }) => setTs(data)).catch(() => {}); }, []);
  const paidAr = Math.max(0, (d.ar_total || 0) - (d.ar_overdue || 0));
  const tsData = (ts?.series || []).map((s) => ({ name: s.period, sales: s.sales, invoices: s.invoice_count }));
  return (
    <div className="space-y-6 page-fade">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <DashCard label="Pesanan Aktif" value={d.so_active ?? "—"} accent="slate" />
        <DashCard label="Invoice Belum Dibayar" value={d.invoice_unpaid ?? "—"} accent="amber" />
        <DashCard label="Total Piutang" value={formatRupiah(d.ar_total)} accent="emerald" />
        <DashCard label="Piutang Terlambat" value={formatRupiah(d.ar_overdue)} accent="rose" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <BarCard
          title="Snapshot Aktivitas Penjualan"
          testId="chart-sales-snapshot"
          hint="Belum ada data penjualan untuk periode ini."
          data={[
            { name: "Pesanan Aktif",   value: d.so_active       || 0 },
            { name: "Invoice Unpaid",  value: d.invoice_unpaid  || 0 },
            { name: "Penerimaan Bulan Ini", value: d.receipts_month || 0 },
          ]}
          bars={[{ key: "value", label: "Jumlah", color: CHART_COLORS.baseline }]}
        />
        <DonutCard
          title="Komposisi Piutang"
          testId="chart-sales-ar"
          hint="Belum ada piutang terbuka."
          formatter={(v) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(v)}
          data={[
            { name: "Belum Jatuh Tempo", value: paidAr,          color: CHART_COLORS.positive },
            { name: "Terlambat",         value: d.ar_overdue || 0, color: CHART_COLORS.risk },
          ]}
        />
      </div>
      <div className="grid grid-cols-1 gap-4">
        <LineCard
          title="Tren Omzet Penjualan 12 Bulan"
          testId="chart-sales-timeseries"
          hint="Belum ada riwayat penjualan 12 bulan terakhir."
          currency
          data={tsData}
          lines={[{ key: "sales", label: "Omzet", color: CHART_COLORS.baseline }]}
        />
      </div>
      <ModuleHub title="Penjualan" description="Kelola pesanan, invoice, nota kredit, dan uang muka pelanggan."
        tiles={[
          { path: "/penjualan/pesanan", label: "Pesanan Penjualan", icon: ShoppingCart, desc: "Draf, ajukan, dan setujui pesanan." },
          { path: "/penjualan/invoice", label: "Invoice Penjualan", icon: Receipt, desc: "Tagihkan pesanan dan pantau status pembayaran." },
          { path: "/penjualan/nota-kredit", label: "Nota Kredit", icon: FileText, desc: "Kurangi piutang atas retur/koreksi." },
          { path: "/penjualan/uang-muka", label: "Uang Muka Pelanggan", icon: Wallet, desc: "Terima uang muka sebelum invoice." },
        ]} />
    </div>
  );
}

export function PurchaseHub() {
  const [d, setD] = useState({});
  const [ts, setTs] = useState(null);
  useEffect(() => { api.get("/txn/dashboard/purchase").then(({ data }) => setD(data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/txn/dashboard/purchase/timeseries?granularity=month").then(({ data }) => setTs(data)).catch(() => {}); }, []);
  const paidAp = Math.max(0, (d.ap_total || 0) - (d.ap_overdue || 0));
  const tsData = (ts?.series || []).map((s) => ({ name: s.period, purchase: s.purchase, bills: s.bill_count }));
  return (
    <div className="space-y-6 page-fade">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <DashCard label="Pesanan Aktif" value={d.po_active ?? "—"} accent="slate" />
        <DashCard label="Tagihan Belum Dibayar" value={d.bill_unpaid ?? "—"} accent="amber" />
        <DashCard label="Total Hutang" value={formatRupiah(d.ap_total)} accent="emerald" />
        <DashCard label="Hutang Terlambat" value={formatRupiah(d.ap_overdue)} accent="rose" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <BarCard
          title="Snapshot Aktivitas Pembelian"
          testId="chart-purchase-snapshot"
          hint="Belum ada data pembelian untuk periode ini."
          data={[
            { name: "Pesanan Aktif",      value: d.po_active       || 0 },
            { name: "Tagihan Unpaid",     value: d.bill_unpaid     || 0 },
            { name: "Pembayaran Bln Ini", value: d.payments_month  || 0 },
          ]}
          bars={[{ key: "value", label: "Jumlah", color: CHART_COLORS.baseline }]}
        />
        <DonutCard
          title="Komposisi Hutang"
          testId="chart-purchase-ap"
          hint="Belum ada hutang terbuka."
          formatter={(v) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(v)}
          data={[
            { name: "Belum Jatuh Tempo", value: paidAp,            color: CHART_COLORS.positive },
            { name: "Terlambat",         value: d.ap_overdue || 0, color: CHART_COLORS.risk },
          ]}
        />
      </div>
      <div className="grid grid-cols-1 gap-4">
        <LineCard
          title="Tren Pembelian 12 Bulan"
          testId="chart-purchase-timeseries"
          hint="Belum ada riwayat pembelian 12 bulan terakhir."
          currency
          data={tsData}
          lines={[{ key: "purchase", label: "Pembelian", color: CHART_COLORS.baseline }]}
        />
      </div>
      <ModuleHub title="Pembelian" description="Kelola pesanan pembelian, tagihan pemasok, nota debit, dan uang muka."
        tiles={[
          { path: "/pembelian/pesanan", label: "Pesanan Pembelian", icon: ShoppingCart, desc: "Buat PO dan setujui." },
          { path: "/pembelian/tagihan", label: "Tagihan Pemasok", icon: Receipt, desc: "Catat tagihan dari pemasok." },
          { path: "/pembelian/nota-debit", label: "Nota Debit", icon: FileText, desc: "Kurangi hutang atas retur/koreksi." },
          { path: "/pembelian/uang-muka", label: "Uang Muka Pemasok", icon: Wallet, desc: "DP kepada pemasok sebelum tagihan." },
          { path: "/pembelian/beban", label: "Beban & Pengeluaran", icon: TrendingDown, desc: "Catat beban langsung/tempo." },
        ]} />
    </div>
  );
}

export function FinanceHub() {
  const [d, setD] = useState({});
  const [cf, setCf] = useState(null);
  useEffect(() => { api.get("/txn/dashboard/finance").then(({ data }) => setD(data)).catch(() => {}); }, []);
  useEffect(() => {
    const to = new Date().toISOString().slice(0, 10);
    const from = new Date(Date.now() - 365 * 24 * 3600 * 1000).toISOString().slice(0, 10);
    api.get(`/fin/cashflow/timeseries?date_from=${from}&date_to=${to}&granularity=month`)
      .then(({ data }) => setCf(data)).catch(() => {});
  }, []);
  const bankRows = (d.bank_balances || []).map((b) => ({ name: `${b.code} · ${b.bank_name}`, value: Number(b.balance) || 0 }));
  const cfData = (cf?.series || []).map((s) => ({ name: s.period, cash_in: s.cash_in, cash_out: s.cash_out, net: s.net_cashflow }));
  return (
    <div className="space-y-6 page-fade">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <DashCard label="Penerimaan Hari Ini" value={formatRupiah(d.receipts_today)} accent="emerald" />
        <DashCard label="Pembayaran Hari Ini" value={formatRupiah(d.payments_today)} accent="amber" />
        <DashCard label="Penerimaan Bulan Ini" value={formatRupiah(d.receipts_month)} accent="emerald" />
        <DashCard label="Pembayaran Bulan Ini" value={formatRupiah(d.payments_month)} accent="amber" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <BarCard
          title="Cash In vs Cash Out"
          testId="chart-cash-inout"
          currency
          hint="Belum ada transaksi kas/bank untuk periode ini."
          data={[
            { name: "Hari Ini",  "Cash In": d.receipts_today || 0, "Cash Out": d.payments_today || 0 },
            { name: "Bulan Ini", "Cash In": d.receipts_month || 0, "Cash Out": d.payments_month || 0 },
          ]}
          bars={[
            { key: "Cash In",  label: "Cash In",  color: CHART_COLORS.positive },
            { key: "Cash Out", label: "Cash Out", color: CHART_COLORS.risk },
          ]}
        />
        <HorizontalBarCard
          title="Saldo per Rekening Bank"
          testId="chart-bank-balance"
          currency
          hint="Belum ada rekening bank aktif."
          data={bankRows}
          bars={[{ key: "value", label: "Saldo", color: CHART_COLORS.baseline }]}
          height={Math.max(200, bankRows.length * 48 + 60)}
        />
      </div>
      <div className="grid grid-cols-1 gap-4">
        <LineCard
          title="Tren Cash In vs Cash Out 12 Bulan"
          testId="chart-cash-timeseries"
          hint="Belum ada aktivitas kas/bank 12 bulan terakhir."
          currency
          data={cfData}
          lines={[
            { key: "cash_in", label: "Cash In", color: CHART_COLORS.positive },
            { key: "cash_out", label: "Cash Out", color: CHART_COLORS.risk },
            { key: "net", label: "Net Cashflow", color: CHART_COLORS.baseline },
          ]}
        />
      </div>
      {d.bank_balances && (
        <Card><CardHeader><CardTitle className="font-display">Saldo Operasional Rekening</CardTitle></CardHeader><CardContent>
          <Table><TableHeader><TableRow><TableHead>Kode</TableHead><TableHead>Bank</TableHead><TableHead>Mata Uang</TableHead><TableHead className="text-right">Saldo (est.)</TableHead></TableRow></TableHeader>
          <TableBody>{d.bank_balances.map((b) => (
            <TableRow key={b.code}><TableCell className="font-mono-num">{b.code}</TableCell><TableCell>{b.bank_name}</TableCell><TableCell>{b.currency}</TableCell><TableCell className="text-right font-mono-num">{formatRupiah(b.balance)}</TableCell></TableRow>
          ))}</TableBody></Table>
          <div className="text-[11px] text-slate-500 mt-2">*Perkiraan operasional dari penerimaan, pembayaran, dan transfer. Buku besar final akan tersedia setelah modul Akuntansi aktif.</div>
        </CardContent></Card>
      )}
      <ModuleHub title="Keuangan & Kas/Bank" description="Kelola penerimaan, pembayaran, transfer antar rekening, dan pusat pembayaran."
        tiles={[
          { path: "/kas-bank/penerimaan", label: "Penerimaan", icon: TrendingUp, desc: "Catat penerimaan pelanggan." },
          { path: "/kas-bank/pembayaran", label: "Pembayaran", icon: TrendingDown, desc: "Catat pembayaran pemasok." },
          { path: "/kas-bank/transfer", label: "Transfer Antar Rekening", icon: ArrowLeftRight, desc: "Pindahkan dana antar bank/kas." },
          { path: "/keuangan/pusat-pembayaran", label: "Pusat Pembayaran", icon: HandCoins, desc: "Kalender kewajiban pembayaran." },
        ]} />
    </div>
  );
}

// -------- Expense page --------
const EXPENSE_STATUS_FLOW = ["DRAF","MENUNGGU_PERSETUJUAN","DISETUJUI","DIPOSTING","DITOLAK","DIBATALKAN"];

export function ExpensePage() {
  const { user } = useAuth();
  const role = user?.role;
  const isAdmin = role === "super_admin" || role === "akuntansi";
  const canCreate = ["super_admin","akuntansi","keuangan"].includes(role);
  const canEdit = ["super_admin","akuntansi","keuangan"].includes(role);
  const canSubmit = ["super_admin","akuntansi","keuangan"].includes(role);
  const canApprove = ["super_admin","direktur_utama","direktur_operasional"].includes(role);
  const canPost = ["super_admin","akuntansi"].includes(role);  // JURNAL_POSTING
  const canCancel = ["super_admin","akuntansi","keuangan"].includes(role);

  const [items, setItems] = useState([]);
  const [coa, setCoa] = useState([]);
  const [banks, setBanks] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [filterMethod, setFilterMethod] = useState("all");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailHistory, setDetailHistory] = useState([]);
  const [detailJournal, setDetailJournal] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // {type, item, extra}
  const [actionNote, setActionNote] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, c, b] = await Promise.all([
        api.get("/txn/entries/expense"),
        api.get("/masters/chart_of_accounts"),
        api.get("/masters/bank_accounts"),
      ]);
      setItems(data);
      setCoa(c.data.filter((x) => (x.status === "ACTIVE" || x.is_active) && x.category === "BEBAN"));
      setBanks(b.data.filter((x) => x.status === "ACTIVE" || x.is_active));
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => items.filter((e) => {
    if (filterStatus !== "all") {
      const s = e.accounting_status === "DIPOSTING" ? "DIPOSTING" : e.status;
      if (s !== filterStatus) return false;
    }
    if (filterFrom && e.date < filterFrom) return false;
    if (filterTo && e.date > filterTo) return false;
    if (filterMethod !== "all") {
      const m = e.paid_now ? "LANGSUNG" : "TERMIN";
      if (m !== filterMethod) return false;
    }
    return true;
  }), [items, filterStatus, filterFrom, filterTo, filterMethod]);

  const effectiveStatus = (e) => e.accounting_status === "DIPOSTING" ? "DIPOSTING" : e.status;

  const openCreate = () => setForm({
    date: new Date().toISOString().slice(0,10), currency: "IDR",
    paid_now: true, dpp: 0, amount: 0, coa_code: "", description: "", payee: "",
    bank_or_cash_code: "", bank_or_cash_type: "BANK", method: "Transfer",
  });
  const openEdit = (e) => {
    setEditing(e);
    setForm({
      ...e,
      bank_or_cash_type: e.bank_or_cash_type || "BANK",
      method: e.method || "Transfer",
    });
  };
  const closeForm = () => { setForm(null); setEditing(null); };

  const saveForm = async (postImmediately = false) => {
    try {
      const payload = {
        date: form.date, coa_code: form.coa_code, payee: form.payee,
        description: form.description, department: form.department, project_code: form.project_code,
        currency: form.currency || "IDR", dpp: Number(form.dpp || form.amount || 0),
        tax_code: form.tax_code || null, amount: Number(form.amount || 0),
        paid_now: !!form.paid_now, bank_or_cash_code: form.bank_or_cash_code,
        bank_or_cash_type: form.bank_or_cash_type || "BANK", method: form.method || "Transfer",
        reference_number: form.reference_number, attachments: form.attachments || [],
      };
      let saved;
      if (editing) {
        const { data } = await api.put(`/txn/expense/${editing.id}`, payload);
        saved = data;
        toast.success("Beban berhasil diperbarui.");
      } else {
        const { data } = await api.post("/txn/expense", { ...payload, client_request_id: reqIdOf(form, setForm) });
        saved = data;
        toast.success("Beban berhasil dicatat sebagai Draf.");
      }
      closeForm();
      if (postImmediately && saved && canPost) {
        await api.post(`/acc/post/expense/${saved.id}`);
        toast.success("Draf berhasil langsung diposting ke Buku Besar.");
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doSimpleAction = async (item, action, note) => {
    try {
      let url = "";
      let body = {};
      if (action === "submit") url = `/txn/entries/expense/item/${item.id}/submit`;
      else if (action === "approve") url = `/txn/entries/expense/item/${item.id}/approve`;
      else if (action === "reject") { url = `/txn/entries/expense/item/${item.id}/reject`; body = { note }; }
      else if (action === "cancel") { url = `/txn/entries/expense/item/${item.id}/cancel`; body = { note }; }
      else if (action === "post") url = `/acc/post/expense/${item.id}`;
      else if (action === "delete") {
        await api.delete(`/txn/expense/${item.id}`);
        toast.success("Draf Beban berhasil dihapus.");
        load(); return;
      } else if (action === "reverse") {
        await api.post(`/acc/journals/${item.posted_journal_id}/reverse`, { note });
        toast.success("Reversal jurnal berhasil dibuat.");
        load(); return;
      }
      await api.post(url, body);
      const labels = { submit: "diajukan", approve: "disetujui", reject: "ditolak", cancel: "dibatalkan", post: "diposting ke Buku Besar" };
      toast.success(`Transaksi berhasil ${labels[action] || action}.`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openDetail = async (e) => {
    try {
      const { data } = await api.get(`/txn/entries/expense/item/${e.id}`);
      setDetail(data.transaction);
      setDetailHistory(data.history || []);
      if (data.transaction?.posted_journal_id) {
        try {
          const j = await api.get(`/acc/journals/${data.transaction.posted_journal_id}`);
          setDetailJournal(j.data);
        } catch { setDetailJournal(null); }
      } else {
        setDetailJournal(null);
      }
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const askConfirm = (type, item, extra = {}) => { setConfirmAction({ type, item, extra }); setActionNote(""); };
  const runConfirm = async () => {
    if (!confirmAction) return;
    const { type, item } = confirmAction;
    if (["reject","cancel","reverse"].includes(type) && !actionNote.trim()) {
      toast.error("Alasan wajib diisi.");
      return;
    }
    await doSimpleAction(item, type, actionNote.trim());
    setConfirmAction(null); setActionNote("");
  };

  const methodLabel = (e) => e.paid_now ? "Langsung" : "Termin";

  return (
    <div className="space-y-6 page-fade" data-testid="expense-page">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Beban & Pengeluaran</h1>
          <p className="text-slate-500 mt-2">Catat beban operasional dengan workflow Draf → Diajukan → Disetujui → Terposting.</p>
        </div>
        {canCreate && (
          <Button onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-expense">
            <Plus className="h-4 w-4 mr-1.5" /> Catat Beban
          </Button>
        )}
      </div>

      <Card><CardContent className="p-4 sm:p-6 space-y-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="space-y-1"><Label className="text-xs">Status</Label>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-48" data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Status</SelectItem>
                <SelectItem value="DRAF">Draf</SelectItem>
                <SelectItem value="MENUNGGU_PERSETUJUAN">Diajukan</SelectItem>
                <SelectItem value="DISETUJUI">Disetujui</SelectItem>
                <SelectItem value="DIPOSTING">Terposting</SelectItem>
                <SelectItem value="DITOLAK">Ditolak</SelectItem>
                <SelectItem value="DIBATALKAN">Dibatalkan</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1"><Label className="text-xs">Dari Tanggal</Label>
            <Input type="date" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} className="w-40" data-testid="filter-from" />
          </div>
          <div className="space-y-1"><Label className="text-xs">Sampai Tanggal</Label>
            <Input type="date" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} className="w-40" data-testid="filter-to" />
          </div>
          <div className="space-y-1"><Label className="text-xs">Metode</Label>
            <Select value={filterMethod} onValueChange={setFilterMethod}>
              <SelectTrigger className="w-40" data-testid="filter-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua</SelectItem>
                <SelectItem value="LANGSUNG">Langsung</SelectItem>
                <SelectItem value="TERMIN">Termin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={load}>Muat Ulang</Button>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Nomor</TableHead><TableHead>Tanggal</TableHead><TableHead>Akun</TableHead>
              <TableHead>Deskripsi</TableHead><TableHead className="text-right">Nominal</TableHead>
              <TableHead>Metode</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {loading ? <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-500">Memuat data...</TableCell></TableRow>
              : filtered.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-500">Belum ada data beban.</TableCell></TableRow>
              : filtered.map((e) => {
                const es = effectiveStatus(e);
                return (
                  <TableRow key={e.id} data-testid={`expense-row-${e.id}`}>
                    <TableCell className="font-mono-num text-sm">{e.number}</TableCell>
                    <TableCell>{formatDateID(e.date)}</TableCell>
                    <TableCell className="font-mono-num text-sm">{e.coa_code}</TableCell>
                    <TableCell className="max-w-xs truncate" title={e.description}>{e.description || "-"}</TableCell>
                    <TableCell className="text-right font-mono-num">{formatRupiah(e.amount)}</TableCell>
                    <TableCell>{methodLabel(e)}</TableCell>
                    <TableCell><StatusBadge status={es} /></TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`expense-actions-${e.id}`}><MoreHorizontal className="h-4 w-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-white">
                          <DropdownMenuItem onClick={() => openDetail(e)} data-testid={`action-detail-${e.id}`}>
                            <FileText className="h-4 w-4 mr-2" /> Detail
                          </DropdownMenuItem>
                          {es === "DRAF" && <>
                            {canEdit && <DropdownMenuItem onClick={() => openEdit(e)} data-testid={`action-edit-${e.id}`}>Edit</DropdownMenuItem>}
                            {canEdit && <DropdownMenuItem onClick={() => askConfirm("delete", e)} className="text-rose-600" data-testid={`action-delete-${e.id}`}>
                              <Trash2 className="h-4 w-4 mr-2" /> Hapus
                            </DropdownMenuItem>}
                            {canSubmit && <DropdownMenuSeparator />}
                            {canSubmit && <DropdownMenuItem onClick={() => doSimpleAction(e, "submit")} data-testid={`action-submit-${e.id}`}>
                              <Send className="h-4 w-4 mr-2" /> Ajukan Persetujuan
                            </DropdownMenuItem>}
                            {canPost && <DropdownMenuItem onClick={() => askConfirm("post", e)} className="text-emerald-700" data-testid={`action-post-direct-${e.id}`}>
                              <CheckCircle className="h-4 w-4 mr-2" /> Posting Langsung
                            </DropdownMenuItem>}
                          </>}
                          {es === "MENUNGGU_PERSETUJUAN" && <>
                            {canApprove && <>
                              <DropdownMenuItem onClick={() => doSimpleAction(e, "approve")} className="text-emerald-700" data-testid={`action-approve-${e.id}`}>
                                <CheckCircle2 className="h-4 w-4 mr-2" /> Setujui
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => askConfirm("reject", e)} className="text-rose-600" data-testid={`action-reject-${e.id}`}>
                                <XCircle className="h-4 w-4 mr-2" /> Tolak
                              </DropdownMenuItem>
                            </>}
                            {canCancel && e.created_by_id === user?.id && <DropdownMenuItem onClick={() => askConfirm("cancel", e)} className="text-rose-600">
                              <Ban className="h-4 w-4 mr-2" /> Tarik Pengajuan
                            </DropdownMenuItem>}
                          </>}
                          {es === "DISETUJUI" && <>
                            {canPost && <DropdownMenuItem onClick={() => askConfirm("post", e)} className="text-emerald-700" data-testid={`action-post-${e.id}`}>
                              <CheckCircle className="h-4 w-4 mr-2" /> Posting ke Buku Besar
                            </DropdownMenuItem>}
                            {canCancel && <DropdownMenuItem onClick={() => askConfirm("cancel", e)} className="text-rose-600">
                              <Ban className="h-4 w-4 mr-2" /> Batalkan
                            </DropdownMenuItem>}
                          </>}
                          {es === "DIPOSTING" && <>
                            {e.posted_journal_id && <DropdownMenuItem onClick={() => openDetail(e)} data-testid={`action-view-journal-${e.id}`}>
                              <FileText className="h-4 w-4 mr-2" /> Lihat Jurnal
                            </DropdownMenuItem>}
                            {canPost && e.posted_journal_id && <DropdownMenuItem onClick={() => askConfirm("reverse", e)} className="text-amber-700" data-testid={`action-reverse-${e.id}`}>
                              <ArrowLeftRight className="h-4 w-4 mr-2" /> Buat Reversal/Koreksi
                            </DropdownMenuItem>}
                          </>}
                          {es === "DITOLAK" && canEdit && <DropdownMenuItem onClick={() => openEdit(e)} data-testid={`action-resubmit-${e.id}`}>
                            Edit & Ajukan Ulang
                          </DropdownMenuItem>}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent></Card>

      {/* FORM DIALOG */}
      {form && <Dialog open={!!form} onOpenChange={(v) => !v && closeForm()}>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? "Ubah Beban (Draf)" : "Catat Beban Baru"}</DialogTitle>
            <DialogDescription>Transaksi disimpan sebagai <strong>Draf</strong> terlebih dahulu dan belum memengaruhi Buku Besar sampai diposting.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Tanggal <span className="text-rose-500">*</span></Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="form-date" /></div>
            <div className="space-y-1.5"><Label>Akun Beban (COA) <span className="text-rose-500">*</span></Label>
              <Select value={form.coa_code || ""} onValueChange={(v) => setForm({ ...form, coa_code: v })}>
                <SelectTrigger data-testid="form-coa"><SelectValue placeholder="Pilih akun beban" /></SelectTrigger>
                <SelectContent className="max-h-72">{coa.map((c) => <SelectItem key={c.code} value={c.code}>{c.code} — {c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5 col-span-2"><Label>Deskripsi</Label>
              <Textarea rows={2} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="form-description" /></div>
            <div className="space-y-1.5"><Label>Penerima/Vendor</Label>
              <Input value={form.payee || ""} onChange={(e) => setForm({ ...form, payee: e.target.value })} data-testid="form-payee" /></div>
            <div className="space-y-1.5"><Label>Nominal <span className="text-rose-500">*</span></Label>
              <Input type="number" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value || 0), dpp: parseFloat(e.target.value || 0) })} className="font-mono-num" data-testid="form-amount" /></div>
            <div className="space-y-1.5 col-span-2"><Label>Metode Pembayaran <span className="text-rose-500">*</span></Label>
              <div className="flex gap-4 pt-1">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" checked={!!form.paid_now} onChange={() => setForm({ ...form, paid_now: true })} data-testid="form-method-direct" />
                  Langsung (Kas/Bank)
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" checked={!form.paid_now} onChange={() => setForm({ ...form, paid_now: false })} data-testid="form-method-term" />
                  Termin (Hutang Usaha)
                </label>
              </div>
            </div>
            {form.paid_now ? (
              <div className="space-y-1.5 col-span-2"><Label>Rekening Kas/Bank <span className="text-rose-500">*</span></Label>
                <Select value={form.bank_or_cash_code || ""} onValueChange={(v) => setForm({ ...form, bank_or_cash_code: v })}>
                  <SelectTrigger data-testid="form-bank"><SelectValue placeholder="Pilih rekening" /></SelectTrigger>
                  <SelectContent>{banks.map((b) => <SelectItem key={b.code} value={b.code}>{b.code} — {b.bank_name || b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-1.5 col-span-2"><Label>Jatuh Tempo</Label>
                <Input type="date" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="form-due" />
                <p className="text-xs text-slate-500">Beban dengan termin akan di-posting Debit Akun Beban / Kredit Hutang (2199).</p>
              </div>
            )}
            <div className="space-y-1.5 col-span-2"><Label>Nomor Referensi</Label>
              <Input value={form.reference_number || ""} onChange={(e) => setForm({ ...form, reference_number: e.target.value })} data-testid="form-ref" /></div>
          </div>
          <DialogFooter className="flex-wrap gap-2">
            <Button variant="outline" onClick={closeForm}>Batal</Button>
            <Button onClick={() => saveForm(false)} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="form-save-draft">
              Simpan sebagai Draf
            </Button>
            {!editing && canPost && (
              <Button onClick={() => saveForm(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="form-save-post">
                Simpan & Posting Langsung
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>}

      {/* DETAIL DIALOG */}
      {detail && <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Detail Beban — {detail.number}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <div><span className="text-slate-500">Nomor:</span> <span className="font-mono-num">{detail.number}</span></div>
              <div><span className="text-slate-500">Status:</span> <StatusBadge status={effectiveStatus(detail)} /></div>
              <div><span className="text-slate-500">Tanggal:</span> {formatDateID(detail.date)}</div>
              <div><span className="text-slate-500">Akun:</span> <span className="font-mono-num">{detail.coa_code}</span></div>
              <div className="col-span-2"><span className="text-slate-500">Deskripsi:</span> {detail.description || "-"}</div>
              <div><span className="text-slate-500">Penerima:</span> {detail.payee || "-"}</div>
              <div><span className="text-slate-500">Metode:</span> {detail.paid_now ? "Langsung" : "Termin"}</div>
              <div><span className="text-slate-500">Nominal:</span> <span className="font-mono-num">{formatRupiah(detail.amount)}</span></div>
              <div><span className="text-slate-500">Mata Uang:</span> {detail.currency || "IDR"}</div>
              <div><span className="text-slate-500">Dibuat oleh:</span> {detail.created_by} <span className="text-xs text-slate-400">({detail.created_at && formatDateTimeID(detail.created_at)})</span></div>
              {detail.submitted_by && <div><span className="text-slate-500">Diajukan oleh:</span> {detail.submitted_by} <span className="text-xs text-slate-400">({detail.submitted_at && formatDateTimeID(detail.submitted_at)})</span></div>}
              {detail.approved_by && <div><span className="text-slate-500">Disetujui oleh:</span> {detail.approved_by} <span className="text-xs text-slate-400">({detail.approved_at && formatDateTimeID(detail.approved_at)})</span></div>}
              {detail.rejected_by && <div className="col-span-2"><span className="text-slate-500">Ditolak oleh:</span> {detail.rejected_by} — <span className="text-rose-600">{detail.rejection_note}</span></div>}
              {detail.cancelled_by && <div className="col-span-2"><span className="text-slate-500">Dibatalkan oleh:</span> {detail.cancelled_by} — <span className="text-rose-600">{detail.cancellation_note}</span></div>}
              {detail.posted_by && <div><span className="text-slate-500">Diposting oleh:</span> {detail.posted_by} <span className="text-xs text-slate-400">({detail.posted_at && formatDateTimeID(detail.posted_at)})</span></div>}
              {detail.posted_journal_number && <div><span className="text-slate-500">Nomor Jurnal:</span> <span className="font-mono-num text-emerald-700">{detail.posted_journal_number}</span></div>}
            </div>

            {detailJournal && (
              <div className="space-y-2">
                <h4 className="font-semibold text-sm">Jurnal Debit/Kredit — {detailJournal.number}</h4>
                <div className="overflow-x-auto border rounded-md">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50"><tr>
                      <th className="text-left px-2 py-1">Akun</th>
                      <th className="text-left px-2 py-1">Deskripsi</th>
                      <th className="text-right px-2 py-1">Debit</th>
                      <th className="text-right px-2 py-1">Kredit</th>
                    </tr></thead>
                    <tbody>{(detailJournal.lines || []).map((l, i) => (
                      <tr key={i} className="border-t"><td className="px-2 py-1 font-mono-num">{l.account_code}</td>
                      <td className="px-2 py-1">{l.description}</td>
                      <td className="px-2 py-1 text-right font-mono-num">{l.debit ? formatRupiah(l.debit) : "-"}</td>
                      <td className="px-2 py-1 text-right font-mono-num">{l.credit ? formatRupiah(l.credit) : "-"}</td></tr>
                    ))}</tbody>
                    <tfoot className="bg-slate-50 font-semibold"><tr>
                      <td className="px-2 py-1" colSpan={2}>Total</td>
                      <td className="px-2 py-1 text-right font-mono-num">{formatRupiah(detailJournal.total_debit)}</td>
                      <td className="px-2 py-1 text-right font-mono-num">{formatRupiah(detailJournal.total_credit)}</td>
                    </tr></tfoot>
                  </table>
                </div>
              </div>
            )}

            {detailHistory.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-semibold text-sm">Riwayat Audit</h4>
                <div className="border rounded-md divide-y">
                  {detailHistory.map((h) => (
                    <div key={h.id} className="px-3 py-2 text-xs">
                      <div className="flex justify-between"><span className="font-semibold">{h.activity}</span><span className="text-slate-500">{formatDateTimeID(h.timestamp)}</span></div>
                      <div className="text-slate-600">oleh {h.user_name} {h.note && <>— <em>{h.note}</em></>}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDetail(null)}>Tutup</Button></DialogFooter>
        </DialogContent>
      </Dialog>}

      {/* CONFIRM DIALOG */}
      {confirmAction && <Dialog open={!!confirmAction} onOpenChange={(v) => !v && setConfirmAction(null)}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>
              {confirmAction.type === "post" && "Posting Beban ke Buku Besar?"}
              {confirmAction.type === "delete" && "Hapus Draf Beban?"}
              {confirmAction.type === "reject" && "Tolak Pengajuan Beban"}
              {confirmAction.type === "cancel" && "Batalkan Beban"}
              {confirmAction.type === "reverse" && "Buat Jurnal Reversal/Koreksi"}
            </DialogTitle>
            <DialogDescription>
              {confirmAction.type === "post" && "Setelah diposting, transaksi akan memengaruhi Buku Besar dan tidak dapat diedit/dihapus langsung. Koreksi harus melalui jurnal reversal."}
              {confirmAction.type === "delete" && "Draf akan dihapus permanen. Pastikan hanya transaksi Draf yang belum pernah diposting."}
              {confirmAction.type === "reject" && "Transaksi akan dikembalikan ke pembuat untuk diperbaiki. Alasan wajib diisi."}
              {confirmAction.type === "cancel" && "Transaksi akan dibatalkan dan tidak akan membuat jurnal. Alasan wajib diisi."}
              {confirmAction.type === "reverse" && "Jurnal reversal akan dibuat untuk membalik posting ini. Alasan wajib diisi untuk audit trail."}
            </DialogDescription>
          </DialogHeader>
          {["reject","cancel","reverse"].includes(confirmAction.type) && (
            <div className="space-y-1.5"><Label>Alasan <span className="text-rose-500">*</span></Label>
              <Textarea rows={3} value={actionNote} onChange={(e) => setActionNote(e.target.value)} placeholder="Jelaskan alasan..." data-testid="confirm-note" /></div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmAction(null)}>Batal</Button>
            <Button onClick={runConfirm} className={confirmAction.type === "post" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-rose-600 hover:bg-rose-700 text-white"} data-testid="confirm-run">
              {confirmAction.type === "post" && "Posting"}
              {confirmAction.type === "delete" && "Hapus"}
              {confirmAction.type === "reject" && "Tolak"}
              {confirmAction.type === "cancel" && "Batalkan"}
              {confirmAction.type === "reverse" && "Buat Reversal"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>}
    </div>
  );
}

// -------- Transfer page --------
export function TransferPage() {
  const [items, setItems] = useState([]);
  const [banks, setBanks] = useState([]);
  const [form, setForm] = useState(null);
  const load = async () => {
    const [{ data }, b] = await Promise.all([api.get("/txn/entries/bank_transfer"), api.get("/masters/bank_accounts")]);
    setItems(data); setBanks(b.data.filter((x) => x.status === "ACTIVE"));
  };
  useEffect(() => { load(); }, []);
  const submit = async () => {
    try { await api.post("/txn/bank_transfer", { ...form, client_request_id: reqIdOf(form, setForm) }); toast.success("Transfer berhasil dicatat."); setForm(null); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-6 page-fade" data-testid="transfer-page">
      <div className="flex justify-between items-end">
        <div><h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Transfer Antar Rekening</h1><p className="text-slate-500 mt-2">Pindahkan dana antar rekening bank/kas.</p></div>
        <Button onClick={() => setForm({ date: new Date().toISOString().slice(0,10), currency: "IDR", amount: 0, fee: 0 })} className="bg-emerald-600 hover:bg-emerald-700 text-white"><Plus className="h-4 w-4 mr-1.5" /> Buat Transfer</Button>
      </div>
      <Card><CardContent className="p-4 sm:p-6">
        <Table><TableHeader><TableRow><TableHead>Nomor</TableHead><TableHead>Tanggal</TableHead><TableHead>Dari</TableHead><TableHead>Ke</TableHead><TableHead className="text-right">Nominal</TableHead><TableHead className="text-right">Biaya</TableHead></TableRow></TableHeader>
        <TableBody>{items.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">Belum ada transfer.</TableCell></TableRow> : items.map((t) => (
          <TableRow key={t.id}><TableCell className="font-mono-num">{t.number}</TableCell><TableCell>{formatDateID(t.date)}</TableCell><TableCell>{t.from_bank_code}</TableCell><TableCell>{t.to_bank_code}</TableCell><TableCell className="text-right font-mono-num">{formatRupiah(t.amount)}</TableCell><TableCell className="text-right font-mono-num">{formatRupiah(t.fee)}</TableCell></TableRow>
        ))}</TableBody></Table>
      </CardContent></Card>
      {form && <Dialog open={!!form} onOpenChange={(v) => !v && setForm(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Transfer Antar Rekening</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Tanggal</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Nominal</Label><Input type="number" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value || 0) })} className="font-mono-num" /></div>
            <div className="space-y-1.5"><Label>Dari</Label>
              <Select value={form.from_bank_code || ""} onValueChange={(v) => setForm({ ...form, from_bank_code: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{banks.map((b) => <SelectItem key={b.code} value={b.code}>{b.code} — {b.bank_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Ke</Label>
              <Select value={form.to_bank_code || ""} onValueChange={(v) => setForm({ ...form, to_bank_code: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{banks.map((b) => <SelectItem key={b.code} value={b.code}>{b.code} — {b.bank_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Biaya Transfer</Label><Input type="number" min="0" value={form.fee} onChange={(e) => setForm({ ...form, fee: parseFloat(e.target.value || 0) })} className="font-mono-num" /></div>
            <div className="space-y-1.5"><Label>Referensi</Label><Input value={form.reference_number || ""} onChange={(e) => setForm({ ...form, reference_number: e.target.value })} /></div>
            <div className="space-y-1.5 col-span-2"><Label>Catatan</Label><Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>Batal</Button>
            <Button onClick={submit} className="bg-slate-900 hover:bg-slate-800 text-white">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>}
    </div>
  );
}

// -------- Documents overview --------
export function DocumentsPage() {
  return (
    <div className="space-y-6 page-fade" data-testid="documents-page">
      <div><h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Dokumen</h1>
        <p className="text-slate-500 mt-2">Ringkasan seluruh transaksi dan referensi. Lampiran mengacu pada URL/metadata pada setiap transaksi.</p></div>
      <Card><CardContent className="p-6 text-sm text-slate-500">Fitur pengelolaan berkas fisik (unggah file) akan aktif setelah integrasi penyimpanan objek pada tahap berikutnya.
        Saat ini, setiap transaksi memiliki tab lampiran untuk mencatat URL & referensi dokumen pendukung.</CardContent></Card>
    </div>
  );
}
